import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Target, Eye, Users, Heart, ArrowRight, Sparkles, Shield, BookOpen } from "lucide-react";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_companions-hub-2/artifacts/byi3wyhw_Pix1.jpeg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_companions-hub-2/artifacts/xm3vjt0f_TCF_logo.jpg";

const committees = [
  { 
    id: "mosque-dawah", 
    name: "Mosque and Da'wah", 
    description: "Responsible for the management of the mosque and Islamic outreach programs, including religious education and community guidance.",
    icon: "🕌",
    color: "from-emerald-500 to-teal-600",
    bgColor: "bg-emerald-50"
  },
  { 
    id: "project-maintenance", 
    name: "Project and Maintenance", 
    description: "Handles all construction, renovation and maintenance projects to ensure our facilities serve the community effectively.",
    icon: "🔧",
    color: "from-blue-500 to-indigo-600",
    bgColor: "bg-blue-50"
  },
  { 
    id: "welfare-publicity", 
    name: "Welfare and Publicity", 
    description: "Manages welfare programs for the less privileged and handles public relations to spread awareness about our initiatives.",
    icon: "💚",
    color: "from-pink-500 to-rose-600",
    bgColor: "bg-pink-50"
  },
  { 
    id: "finance-investment", 
    name: "Finance and Investment", 
    description: "Oversees financial management, fundraising, and investment opportunities to sustain and grow our foundation.",
    icon: "💰",
    color: "from-amber-500 to-orange-600",
    bgColor: "bg-amber-50"
  },
  { 
    id: "security-ethic", 
    name: "Security and Ethics", 
    description: "Ensures security within our premises and maintains ethical standards in all our operations.",
    icon: "🛡️",
    color: "from-purple-500 to-violet-600",
    bgColor: "bg-purple-50"
  },
];

const About = () => {
  return (
    <div className="bg-stone-50">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-emerald-900"></div>
        <div className="pattern-overlay absolute inset-0"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Discover Our Story
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              About Us
            </h1>
            <p className="text-xl text-primary-100 leading-relaxed">
              Learn about The Companions Foundation and our unwavering commitment to serving humanity through faith, compassion, and action.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-72 h-72 bg-primary-100 rounded-3xl -z-10"></div>
              <img 
                src={HERO_IMAGE} 
                alt="Masjid Zaid Ibn Thabit" 
                className="rounded-3xl shadow-2xl w-full object-cover h-[500px] relative z-10"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-6 z-20">
                <img src={LOGO_URL} alt="TCF Logo" className="w-20 h-20 rounded-full mx-auto mb-3" />
                <p className="text-center font-heading font-semibold text-primary-900">Est. Kaduna, Nigeria</p>
              </div>
            </div>
            <div>
              <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-3">Who We Are</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6 tracking-tight">
                The Companions Foundation
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                The Companions Foundation is a community-focused organization dedicated to serving the Muslim 
                community and humanity at large. Based in Kaduna, Nigeria, we strive to make a positive 
                impact through Islamic education, welfare programs, and community development initiatives.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Our foundation operates through five dedicated committees, each focusing on specific aspects 
                of community service and development. Together, we work towards building a stronger, more 
                compassionate community rooted in Islamic values.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild className="btn-primary" data-testid="about-contact-btn">
                  <Link to="/contact">
                    Get in Touch <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="btn-outline" data-testid="about-donate-btn">
                  <Link to="/#donate">
                    Support Our Cause
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-3">Our Purpose</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
              Mission & Vision
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bento-card p-10" data-testid="mission-card">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center mb-6 shadow-lg">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                To serve humanity through Islamic principles, providing comprehensive support to our community 
                through education, welfare, and development programs that uplift individuals and families 
                while strengthening the bonds of brotherhood and sisterhood.
              </p>
            </Card>

            <Card className="bento-card p-10" data-testid="vision-card">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-6 shadow-lg">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                To be a beacon of hope and service in our community, fostering unity, compassion, and 
                progress while preserving Islamic values and traditions for generations to come. We envision 
                a community where every member thrives.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-3">What Drives Us</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight">Our Core Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              The principles that guide everything we do and every decision we make
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, title: "Compassion", desc: "Serving with love, empathy, and genuine care for every individual", color: "from-rose-500 to-pink-600" },
              { icon: Users, title: "Community", desc: "Building strong bonds and fostering unity among all members", color: "from-blue-500 to-indigo-600" },
              { icon: Target, title: "Excellence", desc: "Striving for the highest standards in all our endeavors", color: "from-emerald-500 to-teal-600" },
              { icon: Shield, title: "Integrity", desc: "Operating with honesty, transparency, and ethical conduct", color: "from-purple-500 to-violet-600" },
            ].map((value, index) => (
              <Card key={index} className="bento-card p-8 text-center group">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${value.color} flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <value.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Committees Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-3">Our Structure</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight">Our Committees</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Five dedicated teams working in harmony to serve our community with excellence
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {committees.map((committee, index) => (
              <Link 
                key={committee.id} 
                to={`/committee/${committee.id}`}
                className="group"
                data-testid={`about-committee-${committee.id}`}
              >
                <Card className="bento-card h-full overflow-hidden">
                  <CardContent className="p-8">
                    <div className={`w-16 h-16 rounded-2xl ${committee.bgColor} flex items-center justify-center text-3xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      {committee.icon}
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-gray-900 mb-3 group-hover:text-primary-900 transition-colors">
                      {committee.name}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4">{committee.description}</p>
                    <div className="flex items-center text-primary-600 font-medium text-sm group-hover:gap-3 gap-1 transition-all">
                      Learn More <ArrowRight className="w-4 h-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-emerald-900"></div>
        <div className="pattern-overlay absolute inset-0"></div>
        <div className="container-custom relative z-10 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6 tracking-tight">
              Want to Support Our Mission?
            </h2>
            <p className="text-primary-200 text-lg mb-10 leading-relaxed">
              Join us in making a difference. Whether through volunteering, donations, or spreading the word, 
              every contribution helps us serve more people and strengthen our community.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                asChild 
                className="bg-white text-primary-900 hover:bg-primary-50 rounded-full px-10 py-6 text-lg font-medium shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1" 
                data-testid="about-cta-btn"
              >
                <Link to="/contact">Contact Us Today</Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white/10 rounded-full px-10 py-6 text-lg font-medium"
              >
                <Link to="/#donate">Make a Donation</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
